/**
 * useProximityWorker — React hook that owns a single background Web Worker
 * for haversine proximity math. Keeps the main thread free so the WebGL
 * map can pan/zoom without stutter.
 *
 * Inputs
 *   me         : { lat, lng } | null       — current smoothed GPS fix
 *   contacts   : [{ user_id, latest:{lat,lng} , ... }]  — from /api/contacts
 *   radiusMiles: number (default 3)
 *
 * Returns
 *   {
 *     distances,   // Map<user_id, number miles>
 *     inside,      // Set<user_id> currently within radius
 *     nearby,      // contacts array filtered to inside, nearest-first, with .distance_miles overwritten
 *     lastRunMs,   // last worker computeMs (for perf debugging)
 *     onAlert,     // subscribe(cb) — fires ({ user_id, kind:'entered'|'left', distance_miles })
 *   }
 *
 * Notes
 *   - We debounce inputs: recompute only when `me` has moved more than ~5 m
 *     OR contacts array identity changed (new list from server poll).
 *   - Only ONE inflight job at a time — newer requests supersede.
 *   - Worker is terminated on unmount.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

const MIN_MOVE_METERS = 5;

function movedMeters(a, b) {
  if (!a || !b) return Infinity;
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const mid = toRad((a.lat + b.lat) / 2);
  const x = dLng * Math.cos(mid);
  return Math.sqrt(x * x + dLat * dLat) * R;
}

export function useProximityWorker({ me, contacts, radiusMiles = 3 }) {
  const workerRef = useRef(null);
  const jobRef = useRef(0);
  const lastMeRef = useRef(null);
  const lastContactsRef = useRef(null);
  const alertListenersRef = useRef(new Set());

  const [state, setState] = useState({
    distances: new Map(),
    inside: new Set(),
    lastRunMs: 0,
  });

  // Create the worker exactly once per component mount.
  useEffect(() => {
    const w = new Worker(new URL('./proximity.worker.js', import.meta.url));
    workerRef.current = w;

    w.onmessage = (evt) => {
      const msg = evt.data || {};
      if (msg.type === 'result') {
        // Ignore stale jobs
        if (msg.jobId !== jobRef.current) return;
        const distances = new Map();
        for (const r of msg.results) {
          if (r.user_id && r.distance_miles != null) distances.set(r.user_id, r.distance_miles);
        }
        setState({
          distances,
          inside: new Set(msg.insideIds),
          lastRunMs: msg.elapsedMs || 0,
        });
      } else if (msg.type === 'alert') {
        for (const cb of alertListenersRef.current) {
          try { cb(msg); } catch { /* listener errors are non-fatal */ }
        }
      }
    };

    return () => {
      w.terminate();
      workerRef.current = null;
    };
  }, []);

  // Extract the minimal coord array we ship to the worker.
  const workerContacts = useMemo(() => {
    if (!Array.isArray(contacts)) return [];
    const out = [];
    for (const c of contacts) {
      const lat = c?.latest?.lat ?? c?.lat;
      const lng = c?.latest?.lng ?? c?.lng;
      if (c?.user_id && lat != null && lng != null) {
        out.push({ user_id: c.user_id, lat, lng });
      }
    }
    return out;
  }, [contacts]);

  // Kick off a job only when meaningful input changes.
  useEffect(() => {
    const w = workerRef.current;
    if (!w) return;
    if (!me || me.lat == null || me.lng == null) return;
    if (workerContacts.length === 0) {
      setState((s) => (s.distances.size === 0 && s.inside.size === 0 ? s : { distances: new Map(), inside: new Set(), lastRunMs: 0 }));
      return;
    }

    const contactsChanged = lastContactsRef.current !== workerContacts;
    const moved = movedMeters(lastMeRef.current, me) >= MIN_MOVE_METERS;
    if (!contactsChanged && !moved) return;

    lastMeRef.current = { lat: me.lat, lng: me.lng };
    lastContactsRef.current = workerContacts;

    jobRef.current += 1;
    w.postMessage({
      type: 'compute',
      jobId: jobRef.current,
      me: { lat: me.lat, lng: me.lng },
      contacts: workerContacts,
      radiusMiles,
    });
  }, [me, workerContacts, radiusMiles]);

  const onAlert = (cb) => {
    alertListenersRef.current.add(cb);
    return () => alertListenersRef.current.delete(cb);
  };

  // Provide a "nearby" convenience: original contact objects, filtered to inside, nearest first.
  const nearby = useMemo(() => {
    if (!Array.isArray(contacts) || state.inside.size === 0) return [];
    const enriched = [];
    for (const c of contacts) {
      if (!state.inside.has(c.user_id)) continue;
      const d = state.distances.get(c.user_id);
      enriched.push({ ...c, distance_miles: d ?? c.distance_miles });
    }
    enriched.sort((a, b) => (a.distance_miles ?? Infinity) - (b.distance_miles ?? Infinity));
    return enriched;
  }, [contacts, state.inside, state.distances]);

  return {
    distances: state.distances,
    inside: state.inside,
    nearby,
    lastRunMs: state.lastRunMs,
    onAlert,
  };
}
